import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysCompanyAccount } from '~/data/company-account/queryKeys';

import { queryKeysCompany } from '../queryKeys';

import { deleteCompanySupplierLetter } from './deleteCompanySupplierLetter';

/**
 * Custom hook for clearing the Company-level supplier letter.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/delete_company_supplier_letter_vadmin_companies__company_id__supplier_letter_delete
 */
export const useMutationDeleteCompanySupplierLetter = (
  options?: Omit<
    UseMutationOptions<void, Error, { companyId: UUID }>,
    'mutationFn' | 'onSuccess'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { companyId: UUID }>({
    async mutationFn({ companyId }) {
      return deleteCompanySupplierLetter(companyId);
    },
    async onSuccess(_data, { companyId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysCompany.getSupplierLetter(companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysCompanyAccount.getSupplierLetters(),
        }),
      ]);
    },
    ...options,
  });
};
